import { createLogger } from './logger'

const logger = createLogger('DebugCore')

/**
 * Debug Mode Core State Management
 * 
 * Provides centralized state for debug mode without React dependencies.
 * This allows state to be shared across all debug components.
 */

// Global debug state
let isDebugEnabled = import.meta.env.VITE_DEBUG_MODE === 'true'
const debugListeners = new Set()

// Component registry and highlight state
const componentRegistry = new Map() // componentId -> { displayName, filePath }
const highlightedComponents = new Set() // Set of highlighted component IDs
const highlightListeners = new Set()

/**
 * Check localStorage on load (persists across page reloads)
 */
if (typeof window !== 'undefined') {
  const storedDebug = localStorage.getItem('debug_mode')
  if (storedDebug !== null) {
    isDebugEnabled = storedDebug === 'true'
  }
  
  // Load highlighted components from localStorage
  const storedHighlights = localStorage.getItem('highlighted_components')
  if (storedHighlights) {
    try {
      const highlights = JSON.parse(storedHighlights)
      highlights.forEach(id => highlightedComponents.add(id))
    } catch (e) {
      logger.warn('Failed to parse highlighted components from localStorage')
    }
  }
}

/**
 * Get current debug enabled state
 */
export function getDebugEnabled() {
  return isDebugEnabled
}

/**
 * Get component registry
 */
export function getComponentRegistry() {
  return componentRegistry
}

/**
 * Get highlighted components set
 */
export function getHighlightedComponents() {
  return highlightedComponents
}

/**
 * Subscribe to debug state changes
 */
export function subscribeToDebug(callback) {
  debugListeners.add(callback)
  return () => debugListeners.delete(callback)
}

/**
 * Subscribe to highlight changes
 */
export function subscribeToHighlights(callback) {
  highlightListeners.add(callback)
  return () => highlightListeners.delete(callback)
}

/**
 * Register a component in the registry
 */
export function registerComponent(componentId, displayName, filePath) {
  componentRegistry.set(componentId, { displayName, filePath })
  highlightListeners.forEach(cb => cb([...componentRegistry.values()]))
}

/**
 * Toggle component highlight state
 */
export function toggleComponentHighlight(componentId) {
  if (highlightedComponents.has(componentId)) {
    highlightedComponents.delete(componentId)
  } else {
    highlightedComponents.add(componentId)
  }
  localStorage.setItem('highlighted_components', JSON.stringify([...highlightedComponents]))
  highlightListeners.forEach(cb => cb([...componentRegistry.values()]))
}

/**
 * Check if component is highlighted
 */
export function isComponentHighlighted(componentId) {
  return highlightedComponents.has(componentId)
}

/**
 * Toggle debug mode at runtime
 */
export function toggleDebugMode() {
  isDebugEnabled = !isDebugEnabled
  localStorage.setItem('debug_mode', isDebugEnabled ? 'true' : 'false')
  debugListeners.forEach(cb => cb(isDebugEnabled))
  logger.info(`Debug mode ${isDebugEnabled ? 'enabled' : 'disabled'}`)
}

/**
 * Generate color based on component name hash
 */
export function getComponentColor(displayName) {
  const hashCode = displayName.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc)
  }, 0)
  const hue = Math.abs(hashCode % 360)
  return {
    border: `hsl(${hue}, 70%, 50%)`,
    background: `hsla(${hue}, 70%, 50%, 0.1)`
  }
}

/**
 * Format props for display
 */
export function formatPropValue(value) {
  try {
    if (typeof value === 'function') {
      return '(function)'
    } else if (typeof value === 'object' && value !== null && value.$$typeof) {
      return '(React element)'
    } else if (value === null) {
      return 'null'
    } else if (value === undefined) {
      return 'undefined'
    } else if (typeof value === 'object') {
      const str = JSON.stringify(value, null, 2)
      return str.length > 100 ? str.substring(0, 100) + '...' : str
    } else {
      return String(value)
    }
  } catch (e) {
    return '(complex object)'
  }
}
